import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { updateProjectDetail } from "@/api/projects"

/**
 * Formats a number with commas for better readability
 * @param num - The number to format
 * @returns Formatted number string with commas
 */
const formatNumberWithCommas = (num: number): string => {
    return num.toLocaleString()
}

/**
 * InteractableSize component
 * @param size - The current size of the project
 * @param project_id - The ID of the project
 * @returns A button that allows the user to change the size of the project
 */
export function InteractableSize({ size, project_id }: { size: number | undefined, project_id: string }) {
    const [currentSize, setCurrentSize] = useState(size || 0)
    const [inputSize, setInputSize] = useState(size || 0)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        setCurrentSize(size || 0)
        setInputSize(size || 0)
    }, [size])

    const handleSizeSubmit = async () => {
        if (inputSize === currentSize) {
            setIsDialogOpen(false)
            return
        }

        // Validate input
        if (inputSize < 0) {
            setError("Size cannot be negative")
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const status = await updateProjectDetail(project_id, "project_size", inputSize.toString())
            console.log("Updating project detail:", {
                project_id,
                newSize: inputSize,
                oldSize: currentSize,
                property: "project_size"
            })

            if (status === 200) {
                setCurrentSize(inputSize)
                setIsDialogOpen(false)
                toast.success("Size updated successfully")
            } else {
                setError("Failed to update size")
                toast.error("Failed to update size")
            }
        } catch (err) {
            setError("An error occurred while updating the size")
            console.error("Error updating project size:", err)
            toast.error("Failed to update size")
        } finally {
            setIsLoading(false)
        }
    }

    const handleCancel = () => {
        setInputSize(currentSize)
        setError(null)
        setIsDialogOpen(false)
    }

    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button 
                    variant="ghost" 
                    className="h-auto p-2 text-gray-800 dark:text-gray-200 font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                    {currentSize ? formatNumberWithCommas(currentSize) : "N/A"}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Change Project Size</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground">
                        Current size: <span className="font-medium">{currentSize ? formatNumberWithCommas(currentSize) :"No size"}</span>
                    </div>
                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                            {error}
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="size-input">New Size</Label>
                        <Input
                            id="size-input"
                            type="number"
                            value={inputSize}
                            onChange={(e) => setInputSize(Number(e.target.value))}
                            min="0"
                            step="1"
                            disabled={isLoading}
                            placeholder="Enter project size"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSizeSubmit()
                                }
                            }}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button 
                        variant="outline" 
                        onClick={handleCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSizeSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? "Updating..." : "Update Size"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
